import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import TagList from '@aurora/shared-client/components/tags/TagEditor/TagList';
import type TagOption from '@aurora/shared-client/components/tags/TagEditor/TagOption';
import type { SearchResultsPageAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type {
  ContextNodeFragment,
  ContextUserFragment
} from '@aurora/shared-generated/types/graphql-types';
import {
  EndUserComponent,
  EndUserPages,
  EndUserQueryParams
} from '@aurora/shared-types/pages/enums';
import React, { useContext } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { searchWidgetFiltersMap } from '../../SearchHelper';
import localStyles from '../SearchWidgetFilters/SearchWidgetFilters.module.pcss';
import useTranslation from '../../../useTranslation';

interface props {
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
  /**
   * The node which search should be scoped to. When not provided, search is scoped to community.
   */
  contextNode?: ContextNodeFragment;
  /**
   * The author(user) data, selected from author filter
   */
  author?: ContextUserFragment;
}

/**
 * Render the applied filter bar
 * @author Amit Agrawal
 */

const SearchAppliedFilters: React.FC<React.PropsWithChildren<props>> = ({
  className,
  contextNode,
  author
}) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.SEARCH_APPLIED_FILTERS
  );
  const { router } = useEndUserRoutes();
  const { community } = useContext(AppContext);

  /**
   * This function helps to retrive the list if applied filters
   *
   * @returns List of applied filters
   */
  function getAppliedFilters() {
    const appliedFilter: TagOption[] = [];
    Object.entries(router.query).forEach(async entry => {
      const [key, value] = entry;
      const filterMenuItemMap = searchWidgetFiltersMap[key];

      if (key === EndUserQueryParams.SEARCH_FILTER_BY_AUTHOR && author) {
        const { login } = author;
        appliedFilter.push({
          label: formatMessage('author', { author: login }),
          value: key
        });
      }

      if (
        key === EndUserQueryParams.SEARCH_FILTER_BY_LOCATION &&
        contextNode &&
        contextNode?.title !== community.title
      ) {
        const { title } = contextNode;
        appliedFilter.push({
          label: formatMessage('location', { location: title }),
          value: key
        });
      }

      if (key === EndUserQueryParams.SEARCH_ONLY_IN_TOPICS && value === 'true') {
        appliedFilter.push({
          label: formatMessage('searchOnlyInTopics'),
          value: key
        });
      }

      if (filterMenuItemMap && filterMenuItemMap[router.query[key] as string]) {
        const label = formatMessage(`${key}.${value}`, { value: value as string });
        appliedFilter.push({
          label,
          value: key
        });
      }

      if (key === EndUserQueryParams.SEARCH_BY_TAGS_FILTER) {
        appliedFilter.push({
          label: formatMessage('tag', { tag: value as string }),
          value: key
        });
      }
    });

    return appliedFilter;
  }

  const appliedFilters = getAppliedFilters();

  if (textLoading || appliedFilters.length === 0) {
    return null;
  }

  /**
   * Function to clear all the applied filters.
   */
  async function handleClearAllFilter() {
    const { query } = router;
    await router.replaceRoute<SearchResultsPageAndParams>(
      EndUserPages.SearchResultsPage,
      null,
      {
        [EndUserQueryParams.SEARCH_KEY]: query[EndUserQueryParams.SEARCH_KEY]
      },
      { shallow: true }
    );
  }

  return (
    <div className={cx(className, 'lia-selected-filters')}>
      <div>
        <TagList
          className={cx('lia-tag')}
          canRemoveTag
          tags={appliedFilters}
          onTagRemove={tag => router.removeQueryParam([tag.value as EndUserQueryParams])}
        />
      </div>
      <Button
        className={cx('lia-clear-all-btn lia-g-loader-btn')}
        variant={ButtonVariant.LINK}
        onClick={() => handleClearAllFilter()}
      >
        {formatMessage('clearAll')}
      </Button>
    </div>
  );
};

export default SearchAppliedFilters;
